import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { getNotifications, markNotificationRead, markAllNotificationsRead } from '../apiClient';

export default function NotificationBell() {
  const { token } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const fetchNotifications = useCallback(async () => {
    if (!token) return;
    const data = await getNotifications(token);
    setNotifications(data.notifications || []);
    setUnreadCount(data.unreadCount || 0);
  }, [token]);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  useEffect(() => {
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const handleMarkRead = async (id) => {
    await markNotificationRead(token, id);
    fetchNotifications();
  };

  const handleMarkAllRead = async () => {
    await markAllNotificationsRead(token);
    fetchNotifications();
  };

  const typeIcons = {
    game_invite: '\uD83C\uDFAE',
    tournament: '\uD83C\uDFC6',
    season: '\uD83C\uDF1F',
    season_reward: '\uD83C\uDFC5',
    tier_boundary: '\uD83D\uDCCA',
    mid_season: '\uD83D\uDCC5',
    reward: '\uD83C\uDF81',
    friend_request: '\uD83E\uDD1D',
    friend_accepted: '\u2705',
    tip_received: '\uD83D\uDCB0',
    game_challenge: '\u2694\uFE0F',
    system: '\uD83D\uDD14'
  };

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        onClick={() => setOpen(!open)}
        style={{
          background: 'transparent', border: 'none', cursor: 'pointer',
          position: 'relative', padding: 4, fontSize: 18, color: '#8b949e'
        }}
      >
        &#128276;
        {unreadCount > 0 && (
          <span style={{
            position: 'absolute', top: -2, right: -4,
            background: '#f85149', color: '#fff', borderRadius: '50%',
            width: 16, height: 16, fontSize: 9, fontWeight: 700,
            display: 'flex', alignItems: 'center', justifyContent: 'center'
          }}>
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div style={{
          position: 'absolute', top: '100%', right: 0, marginTop: 8,
          width: 300, maxHeight: 400, overflowY: 'auto',
          background: '#161b22', border: '1px solid #30363d', borderRadius: 10,
          boxShadow: '0 8px 30px rgba(0,0,0,0.5)', zIndex: 1000
        }}>
          <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            padding: '10px 12px', borderBottom: '1px solid #21262d'
          }}>
            <span style={{ color: '#e2e8f0', fontSize: 13, fontWeight: 600 }}>Notifications</span>
            {unreadCount > 0 && (
              <button onClick={handleMarkAllRead} style={{
                background: 'transparent', border: 'none', color: '#58a6ff',
                fontSize: 11, cursor: 'pointer'
              }}>Mark all read</button>
            )}
          </div>

          {notifications.length === 0 ? (
            <div style={{ padding: 20, textAlign: 'center', color: '#8b949e', fontSize: 12 }}>
              No notifications yet
            </div>
          ) : (
            notifications.slice(0, 20).map(n => (
              <div
                key={n.notification_id}
                onClick={() => !n.is_read && handleMarkRead(n.notification_id)}
                style={{
                  padding: '10px 12px', borderBottom: '1px solid #21262d',
                  background: n.is_read ? 'transparent' : 'rgba(88,166,255,0.05)',
                  cursor: n.is_read ? 'default' : 'pointer'
                }}
              >
                <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                  <span style={{ fontSize: 14 }}>{typeIcons[n.type] || '\uD83D\uDD14'}</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: '#c9d1d9', marginBottom: 2 }}>
                      {n.title}
                    </div>
                    {n.body && (
                      <div style={{ fontSize: 11, color: '#8b949e' }}>{n.body}</div>
                    )}
                    <div style={{ fontSize: 10, color: '#484f58', marginTop: 4 }}>
                      {new Date(n.created_at).toLocaleDateString()}
                    </div>
                  </div>
                  {!n.is_read && (
                    <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#58a6ff', marginTop: 4 }} />
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
